import React from 'react';

// Design System Components
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';

// Types
import { PaginationInfo } from '../../utils';

/**
 * TablePaginationBar — the footer under every Data Review table.
 *
 * ## Why it exists
 *
 * This block was **duplicated byte-for-byte across all seven views** — SKU Matched,
 * MPN/UPC Matched, Unmatched, Ignored Products, Ignored Updates, Processed and
 * Missing Mapping. Same markup, same render guard
 * (`!isLoading && total > perPage`), and a private `PAGE_SIZE_OPTIONS` array
 * re-declared in each file. Only two of the seven comments differed. Migrating the
 * paginator meant editing one identical block seven times, or extracting it once.
 *
 * The guard stays at the CALL SITE. It reads
 * `{!isLoading && pagination.total > pagination.perPage && <TablePaginationBar …/>}`
 * in all seven, exactly as before — moving it in here would change *when* the
 * `Dropdown` unmounts, and that is behaviour, not layout.
 *
 * ## What changed with it (2026-08-31)
 *
 * `Components/Common/PaginatorWithApi` — a `react-paginate` wrapper — became the
 * design system's `Pagination`. Measured side by side against the shipped
 * stylesheet, on the same 42-item / 10-per-page set:
 *
 * | | was | is |
 * |---|---|---|
 * | inactive page number | `#DFE1E6` — **1.24:1** on white | `#505866` — 7.0:1 |
 * | active page chip | `#253858` at `opacity: .7` — a washed slate | `accent-bold` `#091caa` |
 * | target | 32 x 30, off the control grid | 36 x 36 (`md`) |
 * | landmark | none | `<nav aria-label="Pagination">` |
 * | total count | nowhere on screen | "Showing 1–10 of 42" |
 *
 * **1.24:1 is the one that matters** — pages 2..n were very nearly invisible, against
 * the 4.5:1 that SC 1.4.3 asks for. The winning declaration is
 * `.pagination > li > a { color: #DFE1E6 }` at specificity (0,1,2), beating
 * `.pagination a { color: #42526E }` at (0,1,1) three rules below it in the same
 * file. Not a thing you find by reading either rule.
 *
 * And the brand blue was already in that stylesheet: `.pagination-item-bg-blue` sets
 * `#091CAA` at full opacity. Catalog Management never passed the class, so it got the
 * `#253858`/`.7` default — which also fades the white numeral sitting on it.
 *
 * ## The one visible behaviour change
 *
 * The **window algorithm**. `react-paginate` ran `marginPagesDisplayed={2}
 * pageRangeDisplayed={5}`; `Pagination` uses `siblingCount` with first and last always
 * reachable and a constant slot count. At five pages or fewer — which is every tab in
 * the common case — both render every page and the output is identical. Beyond that
 * the ellipsis falls in a different place. Nothing else moves: `onPageChange` was
 * already 1-based at every call site (the old wrapper converted react-paginate's
 * 0-based index internally), and the page-size handler is untouched.
 *
 * `showSummary` is on because the row total had no other home — the old footer showed
 * "Show 10" and a page list, and never said how many records there were.
 *
 * ## Matching Guide/ISR (2026-08-31)
 *
 * `ISRHomePage` — the same DS `Pagination`, one migration earlier — renders the strip
 * at `sm` and right-aligns the whole thing, page-size control included. This now
 * matches it, so the two tables in the product that paginate the same way also look
 * the same doing it. Three consequences worth stating:
 *
 * - **`sm` (28px), not `md`.** A footer is chrome under the data, not a toolbar above
 *   it; 36px controls gave the strip the same weight as the ActionBar at the top of
 *   the page.
 * - **`justify-end`, not `justify-between`.** The page-size control and the page list
 *   are one group — you use them for the same reason — and splitting them across the
 *   full table width made the left-hand control read as unrelated.
 * - **The visible "Show" label is gone**, replaced by `aria-label="Rows per page"` as
 *   on ISR. A bare "10" is less self-explanatory than "Show 10"; the label is one line
 *   away if that turns out to matter, and the accessible name is unaffected either way.
 *
 * ## `disabled` while a page is in flight
 *
 * The strip used to be behind the caller's `!isLoading` guard, so **it unmounted on
 * every page click** — the button vanished from under the pointer, keyboard focus was
 * destroyed (it falls to `<body>` when the focused element is removed), and the row
 * the strip occupied collapsed, so the page jumped. It now stays mounted and takes
 * `disabled`, which `Pagination` already supports. The caller's guard drops `isLoading`
 * and keeps only `total > perPage`.
 */
/** Was re-declared privately in each of the seven views. */
export const PAGE_SIZE_OPTIONS = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
];

export interface TablePaginationBarProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onPageSizeChange: (perPage: string | number) => void;
  /**
   * A page is in flight. The strip stays visible and stops accepting input, rather
   * than unmounting — see the docblock. Keeps focus, keeps the layout still.
   */
  disabled?: boolean;
  /** Distinguishes the seven tabs' bars from one another for Playwright. */
  'data-testid'?: string;
  className?: string;
}

const TablePaginationBar = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  disabled = false,
  'data-testid': dataTestId = 'catalog-pagination',
  className = '',
}: TablePaginationBarProps) => (
  <div
    className={`tw-flex tw-items-center tw-justify-end tw-gap-3 tw-flex-wrap tw-px-4 tw-py-2 tw-border-t tw-border-edge ${className}`}
    data-testid={dataTestId}
  >
    <Dropdown
      id={`${dataTestId}-size`}
      label=""
      aria-label="Rows per page"
      size="sm"
      options={PAGE_SIZE_OPTIONS}
      value={pagination.perPage}
      onChange={onPageSizeChange}
      disabled={disabled}
      className="tw-w-[84px]"
      usePortal
      data-testid={`${dataTestId}-size`}
    />

    {/*
      `Pagination` lays itself out `justify-between` internally, so it has to be sized
      to its content here or it stretches and pushes its own summary away from the
      page numbers.
    */}
    <Pagination
      page={pagination.page}
      totalItems={pagination.total}
      pageSize={pagination.perPage}
      onPageChange={onPageChange}
      size="sm"
      disabled={disabled}
      className="tw-flex-none"
      data-testid={`${dataTestId}-pages`}
    />
  </div>
);

export default TablePaginationBar;
